import React, { useState } from 'react';
import { Badge, Button } from 'react-bootstrap';
import { PlayFill } from 'react-bootstrap-icons';
import { getDueDateStatus, getTaskBorderColor } from '../../shared/utils/taskColors';
import QuickLabelAdd from './QuickLabelAdd';
import './MoSCoWBoard.css';

const MoSCoWBoard = ({ tasks, onToggleTaskStatus, onEditTask, onAddTaskProgress, onUpdateTaskStatus, onStartPomodoro, activeProfileId, onTaskUpdate, taskProgress, setSelectedTask, setShowTaskProgressHistory }) => {
    const [draggedTask, setDraggedTask] = useState(null);
    const [dragOverColumn, setDragOverColumn] = useState(null);

    const columns = {
        MUST: { title: 'Must Have', color: 'danger', tasks: [] },
        SHOULD: { title: 'Should Have', color: 'warning', tasks: [] },
        COULD: { title: 'Could Have', color: 'info', tasks: [] },
        WONT: { title: "Won't Have", color: 'secondary', tasks: [] }
    };

    tasks.forEach(task => {
        const category = task.moscow_category || 'MUST';
        if (columns[category]) {
            columns[category].tasks.push(task);
        }
    });

    const handleDragStart = (e, task) => {
        setDraggedTask(task);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e, columnKey) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverColumn(columnKey);
    };

    const handleDragLeave = () => {
        setDragOverColumn(null);
    };

    const handleDrop = (e, columnKey) => {
        e.preventDefault();
        setDragOverColumn(null);
        
        if (draggedTask && draggedTask.moscow_category !== columnKey) {
            if (onUpdateTaskStatus) {
                onUpdateTaskStatus(draggedTask.id, { moscow_category: columnKey });
            }
        }
        setDraggedTask(null);
    };

    const handleDragEnd = () => {
        setDraggedTask(null);
        setDragOverColumn(null);
    };

    return (
        <div className="moscow-board">
            <h4 className="mb-4">MoSCoW Prioritization</h4>
            <div className="moscow-columns">
                {Object.entries(columns).map(([key, column]) => (
                    <div key={key} className="moscow-column">
                        <div className={`moscow-column-header bg-${column.color} text-white`}>
                            <h6>{column.title}</h6>
                            <Badge bg="light" text="dark">{column.tasks.length}</Badge>
                        </div>
                        <div 
                            className={`moscow-column-body ${dragOverColumn === key ? 'drag-over' : ''}`}
                            onDragOver={(e) => handleDragOver(e, key)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, key)}
                        >
                            {column.tasks.length === 0 ? (
                                <p className="text-muted small text-center mt-3">
                                    {dragOverColumn === key ? 'Drop task here' : 'No tasks'}
                                </p>
                            ) : (
                                column.tasks.map(task => (
                                    <div 
                                        key={task.id} 
                                        className={`moscow-task-card ${getTaskBorderColor(task)} border-start border-3 ${draggedTask?.id === task.id ? 'dragging' : ''}`}
                                        draggable
                                        onDragStart={(e) => handleDragStart(e, task)}
                                        onDragEnd={handleDragEnd}
                                    >
                                        <div className="d-flex align-items-start justify-content-between">
                                            <div onClick={() => onEditTask && onEditTask(task)} className="flex-grow-1">
                                                <div className="task-title">{task.title}</div>
                                                {task.description && <div className="task-description">{task.description}</div>}
                                                <div className="mt-2">
                                                    {task.labels && task.labels.map(label => (
                                                        <span 
                                                            key={label.id} 
                                                            className="badge me-1"
                                                            style={{ 
                                                                backgroundColor: label.color, 
                                                                color: 'white',
                                                                fontSize: '0.75em',
                                                                padding: '0.25em 0.5em',
                                                                borderRadius: '0.375rem'
                                                            }}
                                                        >
                                                            {label.name}
                                                        </span>
                                                    ))}
                                                    <QuickLabelAdd 
                                                        task={task}
                                                        activeProfileId={activeProfileId}
                                                        onTaskUpdate={onTaskUpdate}
                                                    />
                                                </div>
                                                <div className="mt-2 d-flex align-items-center">
                                                    <Badge bg={task.priority === 'URGENT' ? 'danger' : task.priority === 'HIGH' ? 'warning' : 'secondary'} className="me-1">{task.priority}</Badge>
                                                    {task.due_date && (
                                                        <Badge bg={getDueDateStatus(task.due_date)?.color || 'secondary'} className="task-due-date">
                                                            {getDueDateStatus(task.due_date)?.text}
                                                        </Badge>
                                                    )}
                                                </div>
                                                {taskProgress && taskProgress[task.id] && taskProgress[task.id].length > 0 && (
                                                    <div className="mt-1">
                                                        <Badge 
                                                            bg="info" 
                                                            style={{cursor: 'pointer'}}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedTask(task);
                                                                setShowTaskProgressHistory(true);
                                                            }}
                                                        >
                                                            📈 {taskProgress[task.id].length} Progress Entries
                                                        </Badge>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="d-flex flex-column align-items-end gap-2">
                                                {onAddTaskProgress && (
                                                    <Button 
                                                        variant="outline-info" 
                                                        size="sm"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onAddTaskProgress(task);
                                                        }}
                                                        title="Log Progress"
                                                    >
                                                        📈
                                                    </Button>
                                                )}
                                                {onStartPomodoro && task.status !== 'COMPLETED' && (
                                                    <Button 
                                                        variant="outline-success" 
                                                        size="sm"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onStartPomodoro(task);
                                                        }}
                                                        title="Start Pomodoro Timer"
                                                    >
                                                        <PlayFill size={16} />
                                                    </Button>
                                                )}
                                                <input
                                                    type="checkbox"
                                                    checked={task.status === 'COMPLETED'}
                                                    onChange={() => onToggleTaskStatus(task.id, task.status)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default MoSCoWBoard;
